import { from } from 'rxjs';
import { filter } from 'rxjs/operators';
import ApiClient from '@shoflo/api.v2-client';
import { CAMERA_PRESENTER, SOURCE_ROLE_SPEAKER } from '@shoflo/polymer';
import { BROADCAST_APPROVE_ALL_KNOCKS } from 'actions/Types';
import { getBroadcastRouteMatch } from 'actions/utils';
import { CHAT_FULL_ACCESS } from 'constants/Permissions';
import { PRESENTER_BANNER } from 'constants/Banners';
import { KNOCK_ANSWER, KNOCK_FAIL } from 'store/eventConstants';
import { UNASSIGNED } from 'utils/broadcastSourceHelper';
import { recursiveFunction, withTimeout } from 'utils/utils';
import { selectLastPresenterSource } from 'selectors/broadcastSourcesSelector';
import {
  getRecordingStatus,
  getStreamingStatus,
  logProgramChange
} from '@shoflo/studio-redux/utils';
import {
  clearKnock,
  createSources,
  getSources,
  setAttendeeName,
  showNotification,
  showModal
} from '@shoflo/studio-redux/actions';
import {
  setIsInitializing,
  setProgram,
  setUnassignedAttendeePresence,
  updateSource
} from 'actions/Broadcast';

import { ACCEPTED, MAX_RETRIES, PRESENTER } from 'constants/KnockTypes';
import { VENDOR_AGORA } from '@studio/constants/Vendor';
import { UNAUTHORIZED_ERROR } from '@studio/constants/ModalTypes';
import { shouldHaveFeature } from '@studio/feature.flags/featureFlags';

const DEFAULT_ORDER_NUMBER = 0;

/**
 * Emits an event to the accepted Presenter socket
 * BulkAcceptedKnock
 *
 * @param socket
 */
export const bulkAcceptedKnock = socket => store => next => async action => {
  const { dispatch, getState } = store;

  const targetdActions = [`${BROADCAST_APPROVE_ALL_KNOCKS}_SUCCESS`];

  from([action])
    .pipe(filter($action => targetdActions.includes($action.type)))
    .subscribe(async $action => {
      const useAgora =
        shouldHaveFeature('studio.meetingVendor') === VENDOR_AGORA;

      // If using Agora, skip this middleware
      if (useAgora) return next(action);

      // Get all presenter requests and the accepted ones
      const approvedPresenterRequests = $action.payload.filter(
        request => request.type === PRESENTER && request.status === ACCEPTED
      );

      const [firstApprovedPresenterRequest] = approvedPresenterRequests;
      const broadcastId = firstApprovedPresenterRequest?.broadcastId;

      const requestPayload = approvedPresenterRequests.map(req => {
        const payload = {
          userType: 'Presenter',
          presenterId: req.presenterId,
          sessionId: req.sessionId,
          presenterName: req.name
        };

        if (req.sourceId) {
          return {
            sourceId: req.sourceId,
            ...payload
          };
        }

        return payload;
      });

      const apiClient = getApiClient();
      let {
        meeting,
        attendeeRoster,
        program,
        team,
        broadcastSources,
        attendeeCameraStatus,
        broadcastSettings,
        banners,
        broadcastRecordings: { liveRecordings },
        broadcastStreamEndpoints: streamEndpoints
      } = getState();

      const match = getBroadcastRouteMatch(getState);

      const hasChatAccess = team
        ? team.permissions.some(({ name }) => name === CHAT_FULL_ACCESS)
        : false;

      const { teamId, eventId } = match.params;

      // Check to see if the meeting is in session and that there are acceptedKnocks
      if (!meeting.meetingSession) return;

      // Clear the knock from state
      dispatch(clearKnock($action.payload));

      if (!requestPayload?.length) return;

      let createMeetingAttendeesResponse;

      async function createAttendeesRequest() {
        return apiClient.broadcast.createMeetingAttendees(
          teamId,
          eventId,
          broadcastId,
          meeting.meetingResponse.Meeting.MeetingId,
          {
            attendees: requestPayload
          }
        );
      }

      // Create the attendee
      try {
        createMeetingAttendeesResponse = await recursiveFunction(
          createAttendeesRequest,
          MAX_RETRIES
        );
      } catch (e) {
        console.log(e);
        const message = 'Presenters failed to join!';
        dispatch(
          showNotification(message, {
            timeout: 5500,
            type: 'warning'
          })
        );
        approvedPresenterRequests.forEach(req => {
          socket.emit(KNOCK_FAIL, { ...req, knockId: req._id });
        });
        if (e?.response?.status === 401) {
          dispatch(showModal(UNAUTHORIZED_ERROR));
          return;
        }
      }

      if (!createMeetingAttendeesResponse) return;

      const { meetingResponse, mixerAttendeeIds } = meeting;

      const anyRequestIsUnassignedSource = requestPayload.some(
        r => r.sourceId && isUnassignedSource(r.sourceId, broadcastSources)
      );

      const handleProgramUpdate = (requests, config) => {
        let updatedProgram = { ...program };
        dispatch(getSources());

        requests.forEach((item, index) => {
          if (!item.sourceId) return;

          const requestResponse = config[index];
          dispatch(
            setUnassignedAttendeePresence(
              requestResponse.attendeeResponse.Attendee.AttendeeId
            )
          );
          updatedProgram = {
            ...updatedProgram,
            layers: updatedProgram.layers
              ? updatedProgram.layers.map(layer => {
                  if (layer.source._id === item.sourceId) {
                    return {
                      ...layer,
                      source: {
                        ...layer.source,
                        value:
                          requestResponse.attendeeResponse.Attendee.AttendeeId
                      }
                    };
                  }
                  return layer;
                })
              : []
          };
          dispatch(
            updateSource(
              item.sourceId,
              {
                title: item.name,
                description: item.description,
                cameraUnavailable: item.cameraUnavailable
              },
              null
            )
          );
        });

        dispatch(setProgram(updatedProgram));
        logProgramChange(getState, 'bulkAcceptedKnock middleware');
      };

      if (anyRequestIsUnassignedSource) {
        handleProgramUpdate(
          approvedPresenterRequests,
          createMeetingAttendeesResponse
        );
      }

      const handleRequest = (request, config) => {
        const { attendeeResponse, chatToken, presenterToken } = config;
        dispatch(
          setAttendeeName(
            attendeeResponse.Attendee.AttendeeId,
            request.name,
            '',
            SOURCE_ROLE_SPEAKER
          )
        );

        const { AttendeeId: attendeeId } = attendeeResponse.Attendee;
        let { isCameraOn, isMicrophoneOn } = request || {};
        // Presenter should join with camera ON and MIC OFF by default
        if ('boolean' !== typeof isCameraOn) isCameraOn = true;
        if ('boolean' !== typeof isMicrophoneOn) isMicrophoneOn = false;
        // Allow director to override camera status
        const updatedCameraStatus = {
          ...attendeeCameraStatus,
          [attendeeId]: isCameraOn
        };

        // Get presenter banner
        const presenterBanner = banners.find(
          banner => banner.source.value.templateType === PRESENTER_BANNER
        );

        // Streaming/Recording status
        const isRecording = getRecordingStatus(liveRecordings);
        const isStreaming = getStreamingStatus(streamEndpoints);

        const streamRecordStatus = {
          isRecording,
          isStreaming
        };

        const knockPayload = {
          broadcastId: broadcastId,
          meetingResponse,
          attendeeResponse,
          mixerAttendeeIds,
          socketId: request.socketId,
          knockId: request?._id,
          attendeeRoster,
          program,
          chatToken,
          hasChatAccess,
          attendeeCameraStatus: updatedCameraStatus,
          knockAVStatus: {
            canControlMic: isMicrophoneOn
          },
          muteMeetingAudio: broadcastSettings.muteMeetingAudio || false,
          presenterBanner: presenterBanner || null,
          broadcastSettings,
          streamRecordStatus,
          presenterToken
        };

        // Send the meeting and attendee info over the socket
        socket.emit(
          KNOCK_ANSWER,
          knockPayload,
          withTimeout(
            () => {},
            () => {
              socket.emit(KNOCK_ANSWER, knockPayload);
            },
            3000
          )
        );
      };

      // for knocks without source ids, we create new sources
      let { order: lastPresenterOrder = DEFAULT_ORDER_NUMBER } =
        selectLastPresenterSource(getState());

      let currentOrder = lastPresenterOrder;
      const newSources = approvedPresenterRequests
        .map((req, index) => {
          if (req.sourceId) {
            dispatch(
              setIsInitializing(req.sourceId, {
                isInitializing: true,
                isAttendee: false
              })
            );
            return {};
          }
          currentOrder = currentOrder + 1;
          const config = createMeetingAttendeesResponse[index];
          const { attendeeResponse } = config;
          const { AttendeeId: attendeeId } = attendeeResponse.Attendee;
          return {
            order: currentOrder,
            title: req.name,
            type: CAMERA_PRESENTER,
            value: attendeeId,
            description: req?.description,
            userId: req.userId,
            role: SOURCE_ROLE_SPEAKER,
            cameraUnavailable: req.cameraUnavailable || false
          };
        })
        .filter(element => Object.keys(element).length !== 0);

      if (newSources.length > 0) {
        dispatch(createSources(newSources));
      }

      approvedPresenterRequests.map((req, index) => {
        const config = createMeetingAttendeesResponse[index];
        handleRequest(req, config);
        return req;
      });
    });
  return next(action);
};

export default bulkAcceptedKnock;

/** Check if the source is unassigned */
const isUnassignedSource = (sourceId, broadcastSources) => {
  const source = broadcastSources?.sources.find(s => s._id === sourceId);
  return source?.value === UNASSIGNED;
};

function getApiClient() {
  const { hostname, protocol, port } = (window || {}).location;

  return new ApiClient({
    endpoint: `${protocol}//${hostname}:${port}`,
    proxy: true
  });
}
